import { Box, Button, Grid, InputLabel, Stack, TextField, Typography } from '@mui/material';
import InputDate from 'components/InputDate';
import AutocompleteCustom, { PropertyNameOptions, SelectTypeOptions } from 'components/Select';
import SelectUser from 'components/SelectUser';
import moment, { Moment } from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'store/hooks';
import Order from 'types/entities/Order';
import User from 'types/entities/User';
import Scope from 'types/entities/Scope';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MultiSelectUser from 'components/MultiSelectUser';
import { MultiSelectAutoComplete } from 'components/index';
import PurchaseOrderTable from '../PurchaseOrder/PurchaseOrderTable';

type OrderFormProps = {
  handleChange: (e: React.FormEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  handleDateChange: (name: string, date: Moment | null) => void;
  handleSelectOptions: (e: SelectTypeOptions | null, property: PropertyNameOptions) => void;
  handleSelectClient: (client: User | null | undefined) => void;
  handleSelectRepresentative: (user: User | null | undefined) => void;
  handleSwitchSign: (value: boolean) => void;
  handleClientConnectionSwitch: (value: boolean) => void;
  order: Partial<Order> | undefined;
  creators: User[];
  clients: User[];
  consultants: User[];
  selectedConsultants: User[];
  deliveryManagers: User[];
  scopes: Scope[];
  onScopeAdded: (newScope: Scope) => void;
  onScopeArchived: (updatedScope: Scope) => void;
  handleScopesChange: (newSelectedScopes: Scope[]) => void;
  selectedScopes: Scope[];
  onConsultantArchived: (updatedUser: User) => void;
  onDeliveryManagerArchived: (updatedUser: User) => void;
  handleConsultantChange: (newSelectedConsultants: User[], reason?: string) => void;
  handleDeliveryManagerChange: (newSelectedDeliveryManager: User[], reason?: string) => void;
  availableScopes?: Scope[];
  availableConsultants?: User[];
  availableDeliveryManagers?: User[];
  selectedDeliveryManager: User[];
  onScopeDeletionAttempt?: () => void;
  onDMUserDeletionAttempt?: () => void;
  onConsultantUserDeletionAttempt?: () => void;
  variant: 'outlined' | 'filled';
};
const OrderForm = (props: OrderFormProps) => {
  const {
    order,
    handleChange,
    handleDateChange,
    handleSelectOptions,
    handleSelectClient,
    // handleSwitchSign,
    handleClientConnectionSwitch,
    creators,
    handleSelectRepresentative,
    clients,
    consultants,
    deliveryManagers,
    scopes,
    onScopeAdded,
    onScopeArchived,
    handleScopesChange,
    handleConsultantChange,
    handleDeliveryManagerChange,
    onConsultantArchived,
    onDeliveryManagerArchived,
    selectedScopes,
    selectedConsultants,
    selectedDeliveryManager,
    availableScopes,
    availableConsultants,
    availableDeliveryManagers,
    onScopeDeletionAttempt = () => undefined,
    onDMUserDeletionAttempt = () => undefined,
    onConsultantUserDeletionAttempt = () => undefined,
    variant,
  } = props;

  const [t] = useTranslation();
  const appState = useSelector((state) => state.app);

  const userLocale = navigator.language || 'fr-FR';
  const dateFormat = userLocale !== 'en-US' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';

  const formatDate = (date: string | number | Date | Moment | null | undefined): string => {
    if (!date) return '';

    return moment(date).format('YYYY-MM-DD');
  };

  // const formatDate = (date: string | number | Date | Moment | null | undefined): string => {
  //   if (!date) return '';

  //   // If it's already a Moment instance, just format it
  //   if (moment.isMoment(date)) {
  //     return date.format('YYYY-MM-DD');
  //   }

  //   // For all other types, parse with moment using strict mode
  //   return moment(date, moment.ISO_8601, true).format('YYYY-MM-DD');
  // };

  return (
    <Box sx={{ mb: 5 }}>
      <Typography sx={{ mb: 3, fontWeight: 'bold' }}>{t('Enter_professional_info_tm')}</Typography>
      <Grid container spacing={4}>
        {appState.affairs && (
          <>
            <Grid item xs={12} lg={6}>
              <AutocompleteCustom
                label={t('Engagement_by_name_tm')}
                options={appState.affairs}
                optionsLabels="name"
                handleSelectOptions={handleSelectOptions}
                selectedOption={order?.affair}
                propertyName="affair"
                variant="filled"
              />
            </Grid>
            <Grid item xs={12} lg={3}>
              <AutocompleteCustom
                label={t('Engagement_by_otp_code_tm')}
                options={appState.affairs}
                optionsLabels="otp"
                handleSelectOptions={handleSelectOptions}
                selectedOption={order?.affair}
                propertyName="affair"
                variant="filled"
              />
            </Grid>
          </>
        )}
        <Grid item xs={12} lg={6}>
          <InputLabel htmlFor="name_spec">
            {t('Name_spec')}{' '}
            <Typography display="inline" color="error">
              *
            </Typography>
          </InputLabel>
          <TextField
            id="draft_reference"
            name="name_spec"
            value={order?.name_spec}
            onChange={handleChange}
            placeholder={t('Name_spec')}
            fullWidth
            variant="filled"
            required
          />
        </Grid>
        <Grid item xs={12} lg={3}>
          <InputDate
            label={t('Start_date')}
            name="start_date"
            id="start_date"
            placeholder={dateFormat}
            onchange={handleDateChange}
            value={formatDate(order?.start_date)}
            required
            variant="filled"
          />
        </Grid>
        <Grid item xs={12} lg={3}>
          <InputDate
            label={t('End_date')}
            name="end_date"
            id="end_date"
            placeholder={dateFormat}
            onchange={handleDateChange}
            value={formatDate(order?.end_date)}
            required
            min={formatDate(order?.start_date) || moment().format('YYYY-MM-DD')}
            variant="filled"
            showError
          />
        </Grid>

        {/* <Grid item xs={12} lg={6}>
          <InputLabel htmlFor="draft_reference">{t('Draft_reference_text')}</InputLabel>
          <TextField
            id="draft_reference"
            name="draft_reference"
            value={order?.draft_reference ?? 'value'}
            onChange={handleChange}
            placeholder={t('Draft_reference')}
            fullWidth
            variant="filled"
          />
        </Grid>

        <Grid item xs={12} lg={6}>
          <InputLabel htmlFor="ref_spec">
            {t('Ref_spec')}{' '}
            <Typography display="inline" color="error">
              *
            </Typography>
          </InputLabel>
          <TextField
            id="draft_reference"
            name="ref_spec"
            value={order?.ref_spec ?? 'value'}
            onChange={handleChange}
            placeholder={t('Ref_spec')}
            fullWidth
            variant="filled"
            required
          />
        </Grid> */}

        <Grid
          item
          xs={12}
          lg={12}
          sx={{
            p: 1,
            position: 'relative',
            '& .MuiAvatarGroup-root': {
              display: 'flex',
              flexWrap: 'wrap', // Changed from wrap-reverse
              alignItems: 'center',
              justifyContent: 'flex-start', // Changed from flex-end
            },
          }}
        >
          <InputLabel htmlFor="scopes">
            {t('Scopes_tm')}
            <Typography display="inline" color="error">
              *
            </Typography>
          </InputLabel>{' '}
          <MultiSelectAutoComplete
            items={scopes} // Assuming 'scopes' is an array of { label: string, value: string }
            onChange={handleScopesChange}
            value={selectedScopes} // {selectedScopes} // Assuming you have a state 'selectedScopes' for this
            sx={{ width: '49%' }}
            selectAllLabel="Select scopes on the list or create your own"
            variant={variant}
            onScopeAdded={onScopeAdded} // Pass down the callback
            onScopeArchived={onScopeArchived} // Pass down the callback for archiving scopes
            availableScopes={availableScopes}
            onScopeDeletionAttempt={onScopeDeletionAttempt}
            maximumVisible={4}
          />
        </Grid>
      </Grid>

      <Typography sx={{ mb: 3, fontWeight: 'bold' }}>{t('Stake_holders_tm')}</Typography>
      <Grid container spacing={4}>
        {clients && (
          <Grid item xs={12} lg={6}>
            <SelectUser
              users={clients}
              selectedUser={order?.client}
              onSelectUser={handleSelectClient}
              label={t('Client_name_tm')}
              variant="filled"
            />
          </Grid>
        )}

        <Grid
          item
          xs={12}
          lg={6}
          sx={{
            p: 2,
            position: 'relative',
            '& .MuiAvatarGroup-root': {
              display: 'flex',
              flexWrap: 'wrap', // Changed from wrap-reverse
              alignItems: 'center',
              justifyContent: 'flex-start', // Changed from flex-end
            },
          }}
        >
          <InputLabel htmlFor="consultants">
            {t('Consultants_tm')}
            <Typography display="inline" color="error">
              *
            </Typography>
          </InputLabel>
          <MultiSelectUser
            users={consultants}
            id="consultants"
            onSelectUsers={handleConsultantChange}
            selectedUsers={selectedConsultants}
            onUserArchived={onConsultantArchived}
            label="Select Consultant"
            variant={variant}
            availableUsers={availableConsultants || []}
            onUserDeletionAttempt={onConsultantUserDeletionAttempt}
            maximumVisible={3}
          />
        </Grid>

        <Grid
          item
          xs={12}
          lg={6}
          sx={{
            p: 2,
            position: 'relative',
            '& .MuiAvatarGroup-root': {
              display: 'flex',
              flexWrap: 'wrap', // Changed from wrap-reverse
              alignItems: 'center',
              justifyContent: 'flex-start', // Changed from flex-end
            },
          }}
        >
          <InputLabel htmlFor="deliverymangers">
            {t('Delivery_managers_tm')}{' '}
            <Typography display="inline" color="error">
              *
            </Typography>
          </InputLabel>
          <MultiSelectUser
            users={deliveryManagers}
            id="delivery-managers"
            onSelectUsers={handleDeliveryManagerChange}
            selectedUsers={selectedDeliveryManager}
            onUserArchived={onDeliveryManagerArchived}
            label="Select Delivery Managers"
            variant={variant}
            availableUsers={availableDeliveryManagers || []}
            onUserDeletionAttempt={onDMUserDeletionAttempt}
            maximumVisible={3}
          />
        </Grid>

        {clients && (
          <Grid item xs={12} lg={6}>
            <SelectUser
              users={creators}
              id="representative"
              selectedUser={order?.delegue}
              onSelectUser={handleSelectRepresentative}
              label={t('Representative_select_tm')}
              variant="filled"
            />
          </Grid>
        )}
        <Grid item xs={12} lg={6}>
          <Typography color="neutral" sx={{ mb: 1, fontWeight: 500 }}>
            {t('client_connection_platform')}
          </Typography>
          <Stack direction="row" gap={2}>
            <Button
              sx={{ minWidth: 100 }}
              onClick={() => handleClientConnectionSwitch(true)}
              variant={order && order.client_connection ? 'contained' : 'outlined'}
              startIcon={order && order.client_connection ? <CheckCircleIcon /> : null}
            >
              {t('yes')}
            </Button>
            <Button
              sx={{ minWidth: 100 }}
              onClick={() => handleClientConnectionSwitch(false)}
              variant={order && !order.client_connection ? 'contained' : 'outlined'}
              startIcon={order && !order.client_connection ? <CheckCircleIcon /> : null}
            >
              {t('no')}
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <Typography sx={{ mt: 2, mb: 3, fontWeight: 'bold' }}>{t('purchase_order')}</Typography>

      <PurchaseOrderTable />
    </Box>
  );
};
export default OrderForm;
